import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { CalendarEvent, CalendarEventType, CalendarRelatedEntityType } from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'
import { getEventsForDate } from '../utils/calendarHelpers'
import { startOfLocalDay } from '../utils/formatters'

export type CalendarEventPayload = {
  title: string
  start_date: string
  end_date?: string | null
  description?: string | null
  event_type?: CalendarEventType | string | null
  all_day?: boolean | null
  timezone?: string | null
  is_recurring?: boolean | null
  recurrence_rule?: string | null
  reminder_minutes?: number | null
  related_entity_type?: CalendarRelatedEntityType | string | null
  related_entity_id?: string | null
  client_id?: string | null
  client_ids?: string[] | null
  external_id?: string | null
  deal_id?: string | null
  organization_id?: string | null
  location?: string | null
  virtual_meeting_url?: string | null
  color?: string | null
  agency_id?: string
}

export type CalendarFilters = {
  start_date?: string
  end_date?: string
  event_type?: string
  related_entity_type?: string
  related_entity_id?: string
  client_id?: string // Alias for related_entity_id when related_entity_type is 'client'
  /** Rows naming this prospect (college fixtures and their birthday). */
  prospect_id?: string
}

function mapCalendarEvent(record: Record<string, any>): CalendarEvent {
  return {
    id: record.id,
    title: record.title ?? '',
    description: record.description ?? null,
    event_type: record.event_type ?? null,
    start_date: record.start_date ?? '',
    end_date: record.end_date ?? null,
    all_day: record.all_day ?? null,
    timezone: record.timezone ?? null,
    is_recurring: record.is_recurring ?? null,
    recurrence_rule: record.recurrence_rule ?? null,
    reminder_minutes: record.reminder_minutes ?? null,
    reminder_sent: record.reminder_sent ?? null,
    related_entity_type: record.related_entity_type ?? null,
    related_entity_id: record.related_entity_id ?? null,
    client_id: record.client_id ?? null,
    client_ids: Array.isArray(record.client_ids) ? record.client_ids : [],
    prospect_ids: Array.isArray(record.prospect_ids) ? record.prospect_ids : [],
    external_id: record.external_id || null,
    // Only present when the caller expanded them. Name and roster status are
    // taken straight from the client record so the modal never restates a
    // status that has since changed.
    clients: Array.isArray(record.expand?.client_ids)
      ? record.expand.client_ids.map((c: Record<string, any>) => ({
          id: c.id,
          full_name: c.full_name ?? '',
          roster_status: c.roster_status ?? null,
        }))
      : [],
    deal_id: record.deal_id ?? null,
    organization_id: record.organization_id ?? null,
    location: record.location ?? null,
    virtual_meeting_url: record.virtual_meeting_url ?? null,
    color: record.color ?? null,
    agency_id: record.agency_id ?? '',
    user_id: record.user_id ?? '', // PocketBase uses user_id, not owner_id
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

export async function listCalendarEvents(filters?: CalendarFilters) {
  
  const filterParts: string[] = []

  // Agency filtering - only show events from user's agency
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (agencyId) {
    filterParts.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
  }

  if (filters?.start_date) {
    filterParts.push(`start_date >= "${escapeFilterValue(filters.start_date)}"`)
  }
  if (filters?.end_date) {
    filterParts.push(`start_date <= "${escapeFilterValue(filters.end_date)}"`)
  }
  if (filters?.event_type) {
    filterParts.push(`event_type = "${escapeFilterValue(filters.event_type)}"`)
  }
  if (filters?.related_entity_type) {
    filterParts.push(`related_entity_type = "${escapeFilterValue(filters.related_entity_type)}"`)
  }
  if (filters?.related_entity_id) {
    filterParts.push(`related_entity_id = "${escapeFilterValue(filters.related_entity_id)}"`)
  }
  // A client's events: rows that name them singly, rows about them, and
  // synced games where they are one of several clients on the club
  // (client_ids, a multi-relation; `~` matches inside the stored id list).
  if (filters?.client_id) {
    const id = escapeFilterValue(filters.client_id)
    filterParts.push(`(client_id = "${id}" || related_entity_id = "${id}" || client_ids ~ "${id}")`)
  }
  if (filters?.prospect_id) {
    const id = escapeFilterValue(filters.prospect_id)
    filterParts.push(`(prospect_ids ~ "${id}" || (related_entity_type = "prospect" && related_entity_id = "${id}"))`)
  }

  const filter = filterParts.length > 0 ? filterParts.join(' && ') : undefined
    
  const options: any = {
    sort: '+start_date',
    // So a game can list who is in it without a request per client.
    expand: 'client_ids',
  }
  if (filter) {
    options.filter = filter
  }
    
  const data = await pb.collection('calendar_events').getFullList<Record<string, any>>(options)
  return data.map(mapCalendarEvent)
}

export async function getCalendarEvent(eventId: string) {
  const record = await pb.collection('calendar_events').getOne<Record<string, any>>(eventId, {
    expand: 'client_ids',
  })
  return mapCalendarEvent(record)
}

export async function createCalendarEvent(payload: CalendarEventPayload, userId?: string) {
  const user = pb.authStore.model
  if (!user) throw new Error('User not authenticated')

  // Get agency_id - use payload if provided, otherwise get from user
  const agencyId = payload.agency_id || await ensureUserAgency()
  const creatorId = userId || user.id

  const record = await pb.collection('calendar_events').create({
    ...payload,
    user_id: creatorId,
    agency_id: agencyId,
  })
  return mapCalendarEvent(record)
}

export async function updateCalendarEvent(
  eventId: string,
  payload: Partial<CalendarEventPayload>,
) {
  const record = await pb.collection('calendar_events').update(eventId, payload)
  return mapCalendarEvent(record)
}

export async function deleteCalendarEvent(eventId: string) {
  await pb.collection('calendar_events').delete(eventId)
}

/**
 * Events on the local days from today through today + days.
 *
 * The query is padded a day each side because a stored value can sit up to
 * a day away from the local day it belongs to (timed rows are UTC instants,
 * all-day rows midnight UTC); the exact day membership is decided by
 * getEventsForDate. A date-only upper bound compared below every timestamp
 * on the last day, which is why "today" used to return only midnight rows.
 */
export async function getUpcomingEvents(days: number = 7) {
  const first = startOfLocalDay(new Date())
  const last = new Date(first)
  last.setDate(last.getDate() + days)
  const queryStart = new Date(first); queryStart.setDate(queryStart.getDate() - 1)
  const queryEnd = new Date(last); queryEnd.setDate(queryEnd.getDate() + 2)

  const rows = await listCalendarEvents({
    start_date: queryStart.toISOString(),
    end_date: queryEnd.toISOString(),
  })
  const keep = new Set<string>()
  for (const d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    for (const e of getEventsForDate(rows, d)) keep.add(e.id)
  }
  return rows.filter(e => keep.has(e.id))
}

export async function getTodayEvents() {
  return getUpcomingEvents(0)
}

